import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useStudentAuth } from '../../contexts/StudentAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const StudentLogin = () => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [portalEnabled, setPortalEnabled] = useState(true);
  const [disabledMessage, setDisabledMessage] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(true);
  const { login } = useStudentAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const resp = await axios.get(`${API_BASE}/api/student-auth/portal-status`);
        setPortalEnabled(resp.data.enabled !== false);
        setDisabledMessage(resp.data.disabled_message || '');
      } catch (err) {
        // If status check fails, allow login attempt (server will still enforce)
        setPortalEnabled(true);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkStatus();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginId || !password) {
      toast.error('Please enter your login ID and password');
      return;
    }
    setLoading(true);
    try {
      const studentData = await login(loginId, password);
      toast.success(`Welcome, ${studentData.student_name}!`);
      navigate('/my-portal');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed';
      toast.error(detail);
      // If backend returned 403 due to portal disabled, reflect that
      if (err.response?.status === 403) {
        setPortalEnabled(false);
        setDisabledMessage(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #192650 0%, #1e3a5f 50%, #0d1b2a 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20">
            <i className="fas fa-graduation-cap text-3xl text-white"></i>
          </div>
          <h1 className="text-2xl font-bold text-white">Student Portal</h1>
          <p className="text-white/60 text-sm mt-1">Patrick's Edu Care Pvt. Ltd</p>
        </div>

        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm" data-testid="student-login-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Sign In</CardTitle>
            <CardDescription>Enter your Student ID and password to access your portal</CardDescription>
          </CardHeader>
          <CardContent>
            {checkingStatus ? (
              <div className="py-8 text-center text-sm text-gray-500" data-testid="student-login-checking-status">
                <i className="fas fa-spinner fa-spin mr-2"></i>Loading...
              </div>
            ) : !portalEnabled ? (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center"
                data-testid="student-portal-disabled-banner"
              >
                <i className="fas fa-lock text-amber-600 text-2xl mb-2"></i>
                <p className="text-sm font-semibold text-amber-900 mb-1">Student Portal Unavailable</p>
                <p className="text-xs text-amber-800">
                  {disabledMessage || 'Student portal access is currently unavailable. Please contact support.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="loginId">Student ID</Label>
                  <Input
                    id="loginId"
                    data-testid="student-login-id"
                    placeholder="STU-2026-XXXX"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="student-login-password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 text-sm font-semibold"
                  style={{ backgroundColor: '#192650' }}
                  disabled={loading}
                  data-testid="student-login-submit"
                >
                  {loading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Signing in...</> : 'Sign In'}
                </Button>
              </form>
            )}
            <div className="mt-4 text-center">
              <a href="/login" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                Staff Login <i className="fas fa-arrow-right ml-1 text-[10px]"></i>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentLogin;
